import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ApiKeyManagement from './components/ApiKeyManagement';
import AgentLaunchLogs from './components/AgentLaunchLogs';
import DataProcessingHistory from './components/DataProcessingHistory';
import EnvironmentToggle from './components/EnvironmentToggle';
import UserPreferences from './components/UserPreferences';
import SessionManagement from './components/SessionManagement';

const SettingsAdministration = () => {
  const [activeTab, setActiveTab] = useState('api-keys');

  const tabs = [
    {
      id: 'api-keys',
      label: 'API Keys',
      icon: 'Key',
      component: ApiKeyManagement,
      description: 'Manage API keys and authentication tokens'
    },
    {
      id: 'agent-logs',
      label: 'Agent Logs',
      icon: 'FileText',
      component: AgentLaunchLogs,
      description: 'View agent deployment and execution history'
    },
    {
      id: 'data-history',
      label: 'Data Processing',
      icon: 'Database',
      component: DataProcessingHistory,
      description: 'Monitor data processing operations and audit trails'
    },
    {
      id: 'environment',
      label: 'Environment',
      icon: 'Globe',
      component: EnvironmentToggle,
      description: 'Switch between production, sandbox, and development'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      component: UserPreferences,
      description: 'Customize your experience and notifications'
    },
    {
      id: 'sessions',
      label: 'Sessions',
      icon: 'Monitor',
      component: SessionManagement,
      description: 'Manage active login sessions and security'
    }
  ];

  const ActiveComponent = tabs?.find(tab => tab?.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Settings Administration - Ipsos Insight Intelligence Platform</title>
        <meta name="description" content="Comprehensive system configuration and monitoring capabilities for enterprise users. Manage API keys, review agent logs, configure environments, and customize preferences." />
        <meta property="og:title" content="Settings Administration - Ipsos Insight Intelligence Platform" />
        <meta property="og:description" content="Enterprise-grade administrative controls for system configuration, security management, and operational monitoring." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Icon name="Settings" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Settings Administration</h1>
                <p className="text-text-secondary mt-1">
                  Comprehensive system configuration and monitoring capabilities
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <Icon name="Shield" size={16} className="text-success" />
              <span>Enterprise Security Enabled</span>
              <span className="text-border">•</span>
              <Icon name="Clock" size={16} className="text-primary" />
              <span>Last updated: {new Date()?.toLocaleString()}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
                <h3 className="text-md font-semibold text-card-foreground mb-4">Administration</h3>
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-card-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon 
                        name={tab?.icon} 
                        size={18} 
                        className={activeTab === tab?.id ? 'text-primary-foreground' : 'text-muted-foreground'}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{tab?.label}</div>
                        <div className={`text-xs mt-0.5 ${
                          activeTab === tab?.id ? 'text-primary-foreground/80' : 'text-text-secondary'
                        }`}>
                          {tab?.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-8">
                {ActiveComponent && <ActiveComponent />}
              </div>
            </div>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-shrink-0 flex flex-col items-center gap-1 px-4 py-3 min-w-0 ${
                    activeTab === tab?.id
                      ? 'text-primary' :'text-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span className="text-xs font-medium truncate">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsAdministration;